import React from 'react';
import { useHistory } from 'react-router-dom';
import { Stack, Button, CircularProgress, Typography } from '@material-ui/core';
import BackIcon from '@material-ui/icons/ArrowRightAlt';
import CloudDownloadOutlinedIcon from '@material-ui/icons/CloudDownloadOutlined';
import WarningAmberIcon from '@material-ui/icons/WarningAmber';
import { CloudDoneOutlined } from '@material-ui/icons';
import String from './String';
import { useExperience } from '../contexts/experience';
import SetupStep from './SetupStep';
import { useSession } from '../contexts/session';
import useToast from '../hooks/useToast';
import { useLanguage } from '../contexts/language';
import { useQueryParams } from '../hooks/useQueryParams';
import { DownloadExperience } from './DownloadExperience';
import { useTheme } from '../contexts/theme';

export function SetupExperiences(p) {
  const { next } = p;
  const {
    experiences,
    updateSavedExperience,
    selectedExperience,
    selectExperience,
    offline,
    offlineMode,
    user,
  } = useSession();
  const { loadConfig, config } = useExperience();
  const { setLanguage, availableLanguages, content } = useLanguage();
  const history = useHistory();
  const [loading, setLoading] = React.useState(null);
  const [downloading, setDownloading] = React.useState(null);
  const toast = useToast();
  const queryParams = useQueryParams();
  const expIdFromUrl = queryParams.get('expId') ?? '';
  const selectExperienceLabel = content('select_experience');

  const handleSelect = React.useCallback(
    async (experience) => {
      setLoading(experience.id);
      const selected = await selectExperience(experience);
      if (selected?.config) loadConfig(selected.config);
      setLoading(null);
    },
    [selectExperience, loadConfig]
  );

  const handleDownloadCanceled = React.useCallback(async () => {
    setDownloading(null);
    setLoading(null);
  }, []);

  const handleDownloadError = React.useCallback(
    async (experience) => {
      const error = {
        ...experience,
        downloadError: true,
      };
      updateSavedExperience(error);
      handleSelect(error);

      setDownloading(null);
      setLoading(null);
      toast.error(content('download_experience_error'));
    },
    [content]
  );

  const handleDownloadComplete = React.useCallback(
    async (experience) => {
      updateSavedExperience(experience);
      handleSelect(experience);

      setDownloading(null);
      setLoading(null);
      toast.success(content('download_experience_done'));
    },
    [content]
  );

  const handleDownload = React.useCallback(
    async (experience) => {
      if (offline) {
        toast.error(content('download_experierience_config_error'));
        return;
      }
      if (!experience.offlineEnabled) {
        toast.error(content('download_experience_not_allowed'));
        return;
      }
      setLoading(experience.id);
      setDownloading(experience);
    },
    [content, offline]
  );

  React.useEffect(() => {
    if (expIdFromUrl) return; // URL wins; skip auto-select
    if (experiences && experiences.length === 1) handleSelect(experiences[0]);
  }, [expIdFromUrl, experiences, handleSelect]);

  const goToExperience = React.useCallback(
    (exp) => {
      if (!config) return;
      const expLang = exp?.language;
      if (expLang && availableLanguages.includes(expLang)) {
        setLanguage(expLang, { temp: true });
      }
      if (next) {
        history.push(next);
      }
    },
    [config, availableLanguages, setLanguage, next, history]
  );

  const goNext = React.useCallback(
    () => goToExperience(selectedExperience),
    [selectedExperience, goToExperience]
  );

  const navigatedRef = React.useRef(false);

  React.useEffect(() => {
    const loadExpFromUrl = async () => {
      if (!expIdFromUrl) return;
      const experience = experiences.find((exp) => exp.id === expIdFromUrl);
      if (!experience) return;
      // if already selected don't reselect
      if (selectedExperience?.id === experience.id) {
        return;
      }
      await handleSelect(experience);
    };

    loadExpFromUrl();
  }, [expIdFromUrl, experiences, selectedExperience?.id, handleSelect]);

  React.useEffect(() => {
    // if exp ID in URL param and confi has been loaded - redirect
    if (
      !expIdFromUrl ||
      navigatedRef.current ||
      !config ||
      !selectedExperience?.id ||
      selectedExperience.id !== expIdFromUrl
    ) {
      return;
    }

    navigatedRef.current = true;
    goToExperience(selectedExperience);
  }, [expIdFromUrl, config, selectedExperience?.id, goToExperience]);

  return (
    <>
      <SetupStep
        title={selectExperienceLabel}
        action={content('confirm')}
        actionClick={goNext}
        disabled={!selectedExperience?.config || selectedExperience.downloadError || !!loading}
        form={
          <Stack spacing={0.5} width="100%">
            {experiences.length === 0 && (
              <>
                <Typography variant="pretitle" mb={2}>
                  <String s="no_experience" />
                </Typography>
                {!user && offline !== null && (
                  <Button
                    color="secondary"
                    variant="text"
                    startIcon={<BackIcon style={{ transform: 'rotate(-180deg)' }} />}
                    onClick={() => offlineMode(null)}
                    aria-label={content('go_home')}
                  >
                    <String s="go_home" />
                  </Button>
                )}
              </>
            )}

            <Stack spacing={0.5} width="100%" role="radiogroup" aria-label={selectExperienceLabel}>
              {experiences.map((experience) => {
                const isSelected = selectedExperience?.id === experience.id;
                const isLoading = loading === experience.id;
                const isDownloading = downloading?.id === experience.id;
                const isAlreadyDownloaded = experience.config && experience.fromStorage;
                const buttonAriaLabel = isAlreadyDownloaded
                  ? `${content('aria_refresh_experience')}  ${experience.label}`
                  : `${content('aria_download_experience')} ${experience.label}`;
                return (
                  <Stack direction="row" key={experience.id} spacing={0.5}>
                    <Button
                      role="radio"
                      color="secondary"
                      variant={isSelected && !isLoading ? 'contained' : 'outlined'}
                      style={{ paddingLeft: 1, paddingRight: 1 }}
                      fullWidth
                      onClick={() => handleSelect(experience)}
                      disabled={isLoading}
                      data-testclass="experience-button"
                      aria-checked={isSelected || isLoading}
                    >
                      {experience.label}
                    </Button>
                    {(!offline || experience.downloadError) && experience.offlineEnabled && (
                      <Button
                        aria-label={buttonAriaLabel}
                        title={buttonAriaLabel}
                        disabled={isDownloading}
                        onClick={() => handleDownload(experience)}
                        color={experience.downloadError ? 'error' : 'secondary'}
                        style={{ paddingLeft: 1, paddingRight: 1 }}
                        variant={experience.config && loading !== experience.id ? 'contained' : 'outlined'}
                      >
                        {loading === experience.id ? (
                          <CircularProgress
                            color="secondary"
                            size="20px"
                            aria-label={content('aria_loading')}
                          />
                        ) : (
                          <DownloadIcon experience={experience} />
                        )}
                      </Button>
                    )}
                  </Stack>
                );
              })}
            </Stack>
          </Stack>
        }
        themeSwitcher
        stepNav
        {...p}
      />

      {!!downloading && (
        <DownloadExperience
          experience={downloading}
          downloadComplete={handleDownloadComplete}
          downloadError={handleDownloadError}
          downloadCanceled={handleDownloadCanceled}
        />
      )}
    </>
  );
}

const DownloadIcon = ({ experience }) => {
  const { theme } = useTheme();

  if (!experience.config) return <CloudDownloadOutlinedIcon style={{ height: '25px' }} />;
  if (experience.downloadError) return <WarningAmberIcon style={{ height: '25px' }} />;
  if (experience.fromStorage) {
    return <CloudDoneOutlined style={{ height: '25px' }} />;
  }
  return (
    <CloudDoneOutlined
      style={{ height: '25px' }}
      sx={{
        color: theme === 'day' ? '#00FD4E' : '#00b737',
      }}
    />
  );
};
